import type { Node as PMNode, ResolvedPos } from '@atlaskit/editor-prosemirror/model';

/**
 * Expands positions to doc level
 */
export const expandPositionsToDocDepth = ({
	doc,
	startPos,
	endPos,
}: {
	doc: PMNode;
	startPos: number;
	endPos: number;
}): [number, number] => {
	const $startPos = doc.resolve(startPos);
	const $endPos = doc.resolve(endPos);
	const start = $startPos.depth === 0 ? startPos : Math.max($startPos.start(1) - 1, 0);
	const end = $endPos.depth === 0 ? endPos : Math.min($endPos.end(1) + 1, doc.content.size);
	return [start, end];
};

function getWordsFromTextUsingSegmenter(
	text: string,
	locale: string,
): Array<{ text: string; index: number }> {
	const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
	const segments = Array.from(segmenter.segment(text));
	const words = segments.filter((segment) => segment.isWordLike);
	return words.map((word) => ({ text: word.segment, index: word.index }));
}
function getWordsFromTextUsingRegex(text: string): Array<{ text: string; index: number }> {
	const result: Array<{ text: string; index: number }> = [];
	const regex = /\S+/gu;
	const matches = text.matchAll(regex);
	for (const match of matches) {
		result.push({ text: match[0], index: match.index });
	}
	return result;
}

function getWordsFromText(text: string, locale: string): Array<{ text: string; index: number }> {
	if (Intl.Segmenter) {
		return getWordsFromTextUsingSegmenter(text, locale);
	}
	return getWordsFromTextUsingRegex(text);
}

type SelectionPositions = { startPos: ResolvedPos; endPos: ResolvedPos };

function expandSelectionToCompleteWords({
	selectionPositions,
	locale,
}: {
	selectionPositions: SelectionPositions;
	locale: string;
}) {
	const startPos = selectionPositions.startPos.pos;
	const endPos = selectionPositions.endPos.pos;
	let newStartPos = startPos;
	let newEndPos = endPos;

	const parentNodeStartPos = selectionPositions.startPos.start();
	const parentNode = selectionPositions.startPos.parent;

	let startPosTextNode: PMNode | undefined;
	let endPosTextNode: PMNode | undefined;
	let startPosTextNodePos: number | undefined;
	let endPosTextNodePos: number | undefined;
	parentNode.nodesBetween(
		startPos - parentNodeStartPos,
		endPos - parentNodeStartPos,
		(node, nodePos) => {
			if (!startPosTextNode) {
				startPosTextNode = node;
				startPosTextNodePos = nodePos + parentNodeStartPos;
			}
			endPosTextNode = node;
			endPosTextNodePos = nodePos + parentNodeStartPos;
		},
	);

	if (startPosTextNode && startPosTextNode.isText && startPosTextNode.text && startPosTextNodePos) {
		const words = getWordsFromText(startPosTextNode.text, locale);
		const startPosInText = startPos - startPosTextNodePos;
		const selectionStartWord = words.find(
			(word) => word.index < startPosInText && word.index + word.text.length > startPosInText,
		);
		newStartPos = selectionStartWord ? selectionStartWord.index + startPosTextNodePos : startPos;
	}

	if (endPosTextNode && endPosTextNode.isText && endPosTextNode.text && endPosTextNodePos) {
		const words = getWordsFromText(endPosTextNode.text, locale);
		const endPosInText = endPos - endPosTextNodePos;
		const selectionEndWord = words.find(
			(word) => word.index < endPosInText && word.index + word.text.length > endPosInText,
		);
		newEndPos = selectionEndWord
			? selectionEndWord.index + selectionEndWord.text.length + endPosTextNodePos
			: endPos;
	}
	return [newStartPos, newEndPos];
}

function isSelectionWithinSameBlock(selectionPositions: SelectionPositions) {
	return selectionPositions.startPos.sameParent(selectionPositions.endPos);
}

function expandSelectionToCompleteBlocks({
	selectionPositions,
}: {
	selectionPositions: SelectionPositions;
}) {
	const startPos = selectionPositions.startPos.start();
	const endPos = selectionPositions.endPos.end();
	return [startPos, endPos];
}

/**
 * Expands the selection to complete words for AI experience, considering locale.
 *
 * @param {Selection} selection - Selection to expand.
 * @param {string} locale - locale of the product where editor is used.
 * @returns The expanded selection.
 *
 * @example
 * ```ts
 * const expandedSelection = expandSelectionForAIExperience(state, selection, locale);
 * ```
 */
export function expandSelectionForAIExperience({
	selectionPositions,
	locale,
}: {
	selectionPositions: SelectionPositions;
	locale: string;
}) {
	if (isSelectionWithinSameBlock(selectionPositions)) {
		return expandSelectionToCompleteWords({ selectionPositions, locale });
	}
	return expandSelectionToCompleteBlocks({ selectionPositions });
}
